import { useState } from "react";

import { mpan_search_limit } from "../config/constants";
import { searchMultipleMetersByMprn } from "../services/gas.service";
import { MultiMprnResult } from "../types/services.types";
// Import the "searchMultipleMetersByMprn" function to fetch meter details for 1+ MPRNs.
// Import the maximum number of MPRNs allowed per search to implement client-side validation.

// MPRN validation regex: must be between 6-11 digits.
const mprn_regex = /^[0-9]{6,11}$/;

// Error message for invalid MPRN format to be used in both validation and results display.
const invalid_mprn_format_error_message = "Invalid MPRN format (must be 6-11 digits)";

export const useGaslinkMprnSearch = () => {
  // Set up state variables for the MPRN search tab to track input, validation, loading, errors, and fetched results.
  const [mprnInput, setMprnInput] = useState<string>("");
  // Client-side validation error (i.e., before an API call) so the validation is checked instantly.
  const [validationError, setValidationError] = useState<string | null>(null);
  // Server error (i.e., after a failed API call).
  const [mprnError, setMprnError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mprnResults, setMprnResults] = useState<MultiMprnResult[]>([]);

  const handleSearch = async () => {
    // Clear all previous errors and results at the start of a new search.
    setValidationError(null);
    setMprnError(null);
    setMprnResults([]);

    // Parse comma-separated MPRNs and remove any whitespace from each MPRN.
    const mprns = mprnInput
      .split(",")
      .map((mprn) => mprn.trim().replace(/\s/g, ""))
      .filter((mprn) => mprn.length > 0);

    // Check if mprnInput is empty.
    if (mprns.length === 0) {
      setValidationError("Please enter at least one MPRN");
      return;
    }

    // Check if it exceeds the limit for multi MPRN search.
    if (mprns.length > mpan_search_limit) {
      setValidationError(
        `There is a maximum ${String(mpan_search_limit)} MPRNs allowed per search`,
      );
      return;
    }

    // Separate valid and invalid MPRNs based on the regex (must be 6-11 digits).
    // This allows us to give instant feedback for any invalid MPRN/s formats while still searching for valid ones, rather than blocking the entire search or waiting for the API to validate each MPRN.
    const validMprns = mprns.filter((mprn) => mprn_regex.test(mprn));
    const invalidMprns = mprns.filter((mprn) => !mprn_regex.test(mprn));

    // If ALL MPRNs are invalid, display an error.
    if (validMprns.length === 0) {
      setValidationError(`${invalid_mprn_format_error_message}: ${invalidMprns.join(", ")}`);
      return;
    }

    // Display the 'loading' message on the UI.
    setIsLoading(true);

    try {
      // Fetch meter details for valid MPRNs only.
      const results = await searchMultipleMetersByMprn(validMprns);

      // Create failed entries for invalid format MPRNs, so they appear in the results error message.
      const invalidResults = invalidMprns.map((mprn) => ({
        mprn,
        success: false,
        isValid: false,
        gasMeterDetails: null,
        error: invalid_mprn_format_error_message,
      }));

      // Combine API results with invalid format entries so all errors are displayed together.
      setMprnResults([...results, ...invalidResults]);
    } catch (error) {
      // Display any unexpected errors from the API call.
      setMprnError("An error occurred while searching. Please try again.");
      console.log("MPRN search error:", error);
    } finally {
      // Reset the loading state once done.
      setIsLoading(false);
    }
  };

  return {
    mprnInput,
    setMprnInput,
    validationError,
    setValidationError,
    mprnError,
    setMprnError,
    isLoading,
    setIsLoading,
    mprnResults,
    setMprnResults,
    handleSearch,
  };
};
